import { BaseAPIClient } from './base';
import type { Question } from './questions';

// =============================================================================
// TYPES
// =============================================================================

export interface QuestionSetItem {
  id: string;
  questionSetId: string;
  questionId: string;
  order: number;
  question: Question;
}

export interface QuestionSet {
  id: string;
  name: string;
  description: string | null;
  /** Null means this set requires no HTS questionnaire. */
  htsQuestionnaireId: string | null;
  items: QuestionSetItem[];
  createdAt: string;
  updatedAt: string;
}

/** An assessment package that pins a question set — used to warn before delete. */
export interface QuestionSetPackageRef {
  id: string;
  name: string;
}

/** A question set plus the packages that reference it (shape returned by `list`). */
export interface QuestionSetWithPackages extends QuestionSet {
  packages: QuestionSetPackageRef[];
}

export interface QuestionSetItemInput {
  questionId: string;
  order: number;
}

export interface QuestionSetPayload {
  name: string;
  description?: string;
  htsQuestionnaireId?: string | null;
  items: QuestionSetItemInput[];
}

export type UpdateQuestionSetPayload = Partial<QuestionSetPayload>;

// =============================================================================
// SERVICE
// =============================================================================

export class AdminQuestionSetsService extends BaseAPIClient {
  /** Admin: list all question sets. */
  async list(): Promise<QuestionSetWithPackages[]> {
    return this.get<QuestionSetWithPackages[]>('/admin/question-sets');
  }

  /** Admin: get a single question set. */
  async getById(id: string): Promise<QuestionSetWithPackages> {
    return this.get<QuestionSetWithPackages>(`/admin/question-sets/${id}`);
  }

  /** Admin: create a question set. */
  async create(payload: QuestionSetPayload): Promise<QuestionSet> {
    return this.post<QuestionSet>('/admin/question-sets', payload);
  }

  /** Admin: update a question set. */
  async update(id: string, payload: UpdateQuestionSetPayload): Promise<QuestionSet> {
    return this.patch<QuestionSet>(`/admin/question-sets/${id}`, payload);
  }

  /** Admin: delete a question set. */
  async remove(id: string): Promise<void> {
    return this.delete(`/admin/question-sets/${id}`);
  }
}

export default AdminQuestionSetsService;
